import { BASEURL, extendedFetch, isSameOriginAsApi } from './network';
import { getEmailTokenParamsString } from '@utils';
import { sanitizeJsonRequestBody } from '@/utils/jsonSafeForApi';
import { AuthEffectHandler } from '@classes/AuthEffectHandler';
import { PermissionEffectHandler } from '@classes/PermissionHandler';
import { AuthTokenManager } from '@classes/AuthTokenManager';
import { RefreshUnauthorizedError } from '@classes/authErrors';

const appendSearchParams = (url, params) => {
  if (!params || params === '') {
    return url;
  }
  if (url.includes('?')) {
    return url + '&' + params.slice(1, params.length);
  }
  return url + params;
};

export async function sendMultipartApiRequest(method, endpointUrl, body, params_headers) {
  try {
    const url = appendSearchParams(`${BASEURL}/api/${endpointUrl}`, getEmailTokenParamsString());
    const sameOrigin = isSameOriginAsApi(url);

    const runOnce = async (token) => {
      const headers = new Headers();
      headers.set('Accept', 'application/json');
      params_headers?.forEach((e) => {
        headers.set(e[0], e[1]);
      });
      if (token && sameOrigin) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return fetch(url, { method, body, headers, credentials: 'include' });
    };

    let token = AuthTokenManager.getAccessToken();
    let response = await runOnce(token);

    if (sameOrigin && response.status === 401) {
      try {
        const newToken = await AuthTokenManager.refresh(token);
        if (newToken && newToken !== token) {
          response = await runOnce(newToken);
        }
      } catch (e) {
        if (!(e instanceof RefreshUnauthorizedError)) {
          // network error on refresh - return original 401
        }
      }
    }

    // A proxy's 413 or 504 answers with HTML, not JSON. The status is still the useful part, so a
    // body that does not parse keeps it (with the generic error) instead of losing both.
    let data = null;
    try {
      data = await response.json();
    } catch (e) {
      return { status: response.status, data: null, error: 'Something went wrong. Please try again.' };
    }
    return { status: response.status, data };
  } catch (e) {
    console.error(e);
    return {
      error: 'Something went wrong. Please try again.',
    };
  }
}

export function handle400Statuses(status) {
  handle401(status);
  handle403(status);
}

export function handle401(status) {
  if (status === 401) {
    AuthEffectHandler.onLossOfAuth();
  }
}

function handle403(status) {
  if (status === 403) {
    PermissionEffectHandler.onLossOfPermission();
  }
}

export async function sendApiRequest(method, endpointUrl, body, config) {
  let response;
  try {
    const tokenParams = getEmailTokenParamsString();
    const headers = [];

    if (config?.headers) {
      config.headers.forEach((header) => {
        headers.push([[header[0]], header[1]]);
      });
    }

    let bodyNormalized = null;
    if (body) {
      bodyNormalized = JSON.stringify(sanitizeJsonRequestBody(body));
    }

    const url = appendSearchParams(`${BASEURL}/api/${endpointUrl}`, tokenParams);
    response = await extendedFetch(url, method, bodyNormalized, headers);
    const data = await response.json();
    return { status: response.status, data, headers: response.headers, rawResponse: response };
  } catch (e) {
    console.error(e);
    return {
      status: response?.status,
      data: null,
      error: 'Something went wrong. Please try again.',
    };
  }
}

export async function sendApiDownloadRequest(endpointUrl, config) {
  try {
    const tokenParams = getEmailTokenParamsString();
    const headers = [];

    if (config?.headers) {
      config.headers.forEach((header) => {
        headers.push([[header[0]], header[1]]);
      });
    }

    const url = appendSearchParams(`${BASEURL}/api/${endpointUrl}`, tokenParams);
    // `headers` belongs in the 4th slot; passing it as `body` (the 3rd) meant custom headers
    // were silently dropped on every download.
    return extendedFetch(url, 'GET', null, headers, {
      signal: config?.signal,
      redirect: config?.redirect,
    });
  } catch (e) {
    console.error(e);
    return {
      error: 'Something went wrong. Please try again.',
    };
  }
}

export async function sendApiRequestHtml(method, endpointUrl, body, config) {
  try {
    const tokenParams = getEmailTokenParamsString();
    const headers = [];

    let bodyNormalized = null;
    if (body) {
      bodyNormalized = JSON.stringify(sanitizeJsonRequestBody(body));
    }

    const url = appendSearchParams(`${BASEURL}/api/${endpointUrl}`, tokenParams);
    const response = await extendedFetch(url, method, bodyNormalized, headers);
    const data = await response.text();
    return { status: response.status, data };
  } catch (e) {
    console.error(e);
    return {
      error: 'Something went wrong. Please try again.',
    };
  }
}

const STRAPI_BASE_URL = 'https://cms.tornado.com';
const STRAPI_PUBLIC_TOKEN =
  'b98e03cddddba211d7656632e6f1a12dd6d0bc79405baefd32f3280b5657b85d16878fe72d695dd285ba2d8de919166bcc5a3cd2d0faedbd8a6e96373a072b7a5145985952959cb6b5fc37989c6e54ef28b77d735cddf9429685fec2937d927434bce0fec0bae2a7291258876f43f859869aa67dec41d7c674e584e1650f95a6';

export async function getFromCMSApi({ dir, query = '', options = {} }) {
  try {
    const url = `${STRAPI_BASE_URL}/api${dir}${query}`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${STRAPI_PUBLIC_TOKEN}`,
      },
    });
    if (response?.status === 200) {
      const data = await response.json();
      return data;
    } else {
      return {
        error: true,
      };
    }
  } catch (e) {
    console.error(e);
    return {
      error: true,
    };
  }
}
